import React from 'react';
import type { CropModalProps } from './types';
import type { CropState } from './store/cropStore';

// Type exports (always safe for SSR)
export type {
  CropData,
  CropResponse,
  CropPreviewResponse,
  CropModalProps,
  AspectRatioOption,
  BaseCropComponentProps,
} from './types';

// Utility exports (safe for SSR)
export {
  formatDimensions,
  getAspectRatioLabel,
  debounce,
  constrainCrop,
  percentCropToPixelCrop,
  pixelCropToPercentCrop,
} from './utils';

// Clean SSR-safe component
export const CropModal: React.FC<CropModalProps> = (props) => {
  const [Component, setComponent] = React.useState<React.ComponentType<CropModalProps> | null>(
    null
  );
  const [mounted, setMounted] = React.useState(false);

  React.useEffect(() => {
    setMounted(true);

    import('./components/CropModal').then((module) => {
      setComponent(() => module.CropModal);
    });
  }, []);

  if (!mounted || !Component) {
    return null;
  }

  return React.createElement(Component, props);
};

// Clean SSR-safe store
export const useCropStore = (): CropState => {
  const [store, setStore] = React.useState<(() => CropState) | null>(null);
  const [mounted, setMounted] = React.useState(false);

  React.useEffect(() => {
    setMounted(true);

    import('./store/cropStore').then((module) => {
      setStore(() => module.useCropStore);
    });
  }, []);

  // Return mock during SSR or before store loads
  if (!mounted || !store) {
    return {
      originalWidth: 0,
      originalHeight: 0,
      isImageLoaded: false,
      cropPosition: { x: 0, y: 0 },
      zoom: 1,
      croppedArea: null,
      croppedAreaPixels: null,
      aspectRatio: 'original' as const,
      aspectRatioValue: null,
      isSubmitting: false,
      error: null,
      setOriginalDimensions: () => {},
      setImageLoaded: () => {},
      setCropPosition: () => {},
      setZoom: () => {},
      setCroppedArea: () => {},
      setAspectRatio: () => {},
      setSubmitting: () => {},
      setError: () => {},
      resetState: () => {},
      hasSelection: () => false,
      getCropData: () => ({
        x: 0,
        y: 0,
        width: 0,
        height: 0,
        original_width: 0,
        original_height: 0,
        regenerate_variants: true,
      }),
    };
  }

  return store();
};

// Import styles
import './styles.css';
